import React, { useState } from "react";
import axios, { AxiosError } from "axios";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import LanguageSwitcher from "shared/components/LanguageSwitcher";
import { useLanguage } from "shared/context/LanguageContext";
import { clearDraft } from "shared/lib/storage";
import { useCourierAuth } from "features/courier-auth/context/CourierAuthContext";
import {
  type ChallengeResult,
  type CourierOtpMethod,
  type CredentialsResult,
  resendOtp,
  sendOtp,
  verifyCredentials,
  verifyOtp,
} from "features/courier-auth/services/courierAuthService";

import ChannelStep from "../components/ChannelStep";
import CredentialsStep from "../components/CredentialsStep";
import LoginCard, { type LoginStep } from "../components/LoginCard";
import LoginLeftPanel from "../components/LoginLeftPanel";
import OtpStep from "../components/OtpStep";

// Full-colour logo (same asset as the landing header) so it stays legible on
// the white mobile top bar; the dark left panel keeps its own white-tile logo.
const HAAT_LOGO_URL =
  "https://cdn.prod.website-files.com/6829acf870093a50cc6ceb69/682b03419ead6e45d8f2c59e_logo%20(2).svg";

const CourierLoginPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { refresh } = useCourierAuth();
  const { dir } = useLanguage();

  const [step, setStep] = useState<LoginStep>("credentials");
  const [phone, setPhone] = useState("");

  const [credentials, setCredentials] = useState<CredentialsResult | null>(
    null,
  );
  const [selectedChannel, setSelectedChannel] =
    useState<CourierOtpMethod>("sms");

  const [challenge, setChallenge] = useState<ChallengeResult | null>(null);

  const [credentialsError, setCredentialsError] = useState<string | null>(null);
  const [channelError, setChannelError] = useState<string | null>(null);
  const [otpError, setOtpError] = useState<string | null>(null);
  const [isVerifyingCreds, setIsVerifyingCreds] = useState(false);
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [otpSuccess, setOtpSuccess] = useState(false);

  const goTo = (next: LoginStep) => {
    setStep(next);
  };

  const handleVerifyCredentials = async (nextPhone: string) => {
    setPhone(nextPhone);
    setCredentialsError(null);
    setIsVerifyingCreds(true);
    try {
      const result = await verifyCredentials({ phone: nextPhone });
      setCredentials(result);
      setSelectedChannel(
        result.availableMethods.includes("sms")
          ? "sms"
          : (result.availableMethods[0] ?? "sms"),
      );
      setChannelError(null);
      goTo("channel");
    } catch (err) {
      setCredentialsError(
        extractError(err, t("courierAuth.errors.credentials")),
      );
    } finally {
      setIsVerifyingCreds(false);
    }
  };

  const handleSendOtp = async () => {
    if (!credentials || isSendingOtp) return;
    setChannelError(null);
    setIsSendingOtp(true);
    try {
      const result = await sendOtp(credentials.ticketId, selectedChannel);
      setChallenge(result);
      setOtpError(null);
      setOtpSuccess(false);
      goTo("otp");
    } catch (err) {
      setChannelError(extractError(err, t("courierAuth.errors.sendOtp")));
    } finally {
      setIsSendingOtp(false);
    }
  };

  const handleResendOtp = async () => {
    if (!challenge) return;
    try {
      const result = await resendOtp(challenge.challengeId);
      setChallenge(result);
      setOtpError(null);
    } catch (err) {
      setOtpError(extractError(err, t("courierAuth.errors.resend")));
    }
  };

  const handleStartHere = () => {
    clearDraft();
    navigate("/onboarding");
  };

  const handleVerifyOtp = async (code: string) => {
    if (!challenge || isVerifyingOtp || otpSuccess) return;
    setIsVerifyingOtp(true);
    setOtpError(null);
    try {
      await verifyOtp(challenge.challengeId, code);
      await refresh();
      setOtpSuccess(true);
      window.setTimeout(() => navigate("/portal", { replace: true }), 700);
    } catch (err) {
      setOtpError(extractError(err, t("courierAuth.errors.otp")));
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  return (
    <div
      dir="ltr"
      className="flex h-screen w-full overflow-hidden bg-white"
      style={{ height: "100dvh" }}
    >
      <LoginLeftPanel />
      <div
        dir={dir}
        className="flex h-full min-w-0 flex-1 flex-col bg-white"
      >
        <header
          dir="ltr"
          className="flex shrink-0 items-center bg-white px-5 py-4 lg:hidden"
        >
          <img
            src={HAAT_LOGO_URL}
            alt="HAAT"
            className="h-9 w-auto object-contain"
          />
        </header>

        <main className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 py-8 sm:px-8">
          <div className="mx-auto flex min-h-full w-full max-w-md items-center">
            <div className="w-full">
              <LoginCard step={step}>
                {step === "credentials" && (
                  <CredentialsStep
                    initialPhone={phone}
                    isSubmitting={isVerifyingCreds}
                    errorMessage={credentialsError}
                    onEdit={() => setCredentialsError(null)}
                    onSubmit={handleVerifyCredentials}
                  />
                )}
                {step === "channel" && credentials && (
                  <ChannelStep
                    selected={selectedChannel}
                    availableMethods={credentials.availableMethods}
                    maskedPhone={credentials.maskedPhone}
                    maskedEmail={credentials.maskedEmail}
                    isSubmitting={isSendingOtp}
                    errorMessage={channelError}
                    onSelect={setSelectedChannel}
                    onBack={() => goTo("credentials")}
                    onContinue={handleSendOtp}
                  />
                )}
                {step === "otp" && challenge && (
                  <OtpStep
                    sentTo={challenge.sentTo}
                    expiresIn={challenge.otpExpiresIn}
                    isVerifying={isVerifyingOtp}
                    isSuccess={otpSuccess}
                    errorMessage={otpError}
                    devOtp={challenge.devOtp}
                    onBack={() => goTo("channel")}
                    onSubmit={handleVerifyOtp}
                    onResend={handleResendOtp}
                  />
                )}
              </LoginCard>
              <div className="mt-5 flex items-center justify-between gap-4 border-t border-gray-200 pt-4 text-xs text-gray-500">
                <p>
                  {t("courierAuth.footer.notRegistered")}{" "}
                  <button
                    type="button"
                    onClick={handleStartHere}
                    className="font-semibold text-[#bf0035] transition-colors hover:text-[#bf0035]/75"
                  >
                    {t("courierAuth.footer.startHere")}
                  </button>
                </p>
                <LanguageSwitcher variant="compact" />
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

const extractError = (err: unknown, fallback: string): string => {
  if (axios.isAxiosError(err)) {
    const ax = err as AxiosError<{ message?: string | string[] }>;
    const msg = ax.response?.data?.message;
    if (Array.isArray(msg)) return msg.join(" • ");
    if (typeof msg === "string") return msg;
  }
  return fallback;
};

export default CourierLoginPage;
